import {
  getErrorMessage,
  getHttpStatus,
  logFeatureEvent,
  reportFeatureIssue,
  type ObservabilityScope,
} from "./shared";

type FunnelAction =
  | "save"
  | "update"
  | "delete"
  | "run_analysis"
  | "load_suggestions";

type FunnelEventAttributes = {
  funnelId?: string | null;
  funnelName?: string | null;
  stepCount?: number | null;
  crossDomain?: boolean;
  propertyCount?: number | null;
};

const attributesFor = ({
  funnelId,
  funnelName,
  stepCount,
  crossDomain,
  propertyCount,
}: FunnelEventAttributes = {}) => ({
  funnel_id: funnelId ?? null,
  funnel_name: funnelName ?? null,
  step_count: stepCount ?? null,
  cross_domain: crossDomain ?? false,
  property_count: propertyCount ?? null,
});

export const logFunnelEvent = (
  event:
    | "funnel_saved"
    | "funnel_updated"
    | "funnel_deleted"
    | "analysis_run",
  scope: ObservabilityScope,
  attributes?: FunnelEventAttributes,
) =>
  logFeatureEvent({
    feature: "funnels",
    event,
    scope,
    attributes: attributesFor(attributes),
  });

/**
 * A run we refused to send (invalid config, missing auth). The mutation still
 * rejects so the UI can show its error, but the rejection must not reach
 * `reportFeatureIssue` — it is an expected state, already logged as a warn.
 */
export class FunnelAnalysisBlockedError extends Error {
  readonly reason: string;

  constructor(reason: string, message: string) {
    super(message);
    this.name = "FunnelAnalysisBlockedError";
    this.reason = reason;
  }
}

/**
 * Logs the blocked run and returns the error to throw, so a caller can never
 * log one without throwing the other.
 */
export const blockFunnelAnalysis = (
  reason: string,
  message: string,
  scope: ObservabilityScope,
  attributes?: FunnelEventAttributes,
) => {
  logFeatureEvent({
    feature: "funnels",
    event: "analysis_blocked",
    level: "warn",
    message: `Funnels: analysis blocked (${reason})`,
    scope,
    attributes: { reason, ...attributesFor(attributes) },
  });

  return new FunnelAnalysisBlockedError(reason, message);
};

// createFunnelApiError sets `.status`, but some callers still throw a bare
// Error whose only signal is the message (same shape as getRequest).
const ACCESS_DENIED_MESSAGE =
  /\b(?:HTTP(?: error! status:)?\s*(?:401|403)|access denied|forbidden|unauthori[sz]ed)\b/i;

/** Cloudflare / origin timeouts on a long funnel query. Do not retry 524. */
const ANALYSIS_TIMEOUT_STATUSES = new Set([408, 504, 522, 523, 524]);

export const isFunnelAccessDenied = (error: unknown) => {
  const status = getHttpStatus(error);
  if (status === 401 || status === 403) return true;

  return ACCESS_DENIED_MESSAGE.test(getErrorMessage(error));
};

export const isFunnelAnalysisTimeout = (error: unknown) => {
  const status = getHttpStatus(error);
  if (status !== null) return ANALYSIS_TIMEOUT_STATUSES.has(status);

  return /\b(?:408|504|522|523|524)\b/.test(getErrorMessage(error));
};

/**
 * Expected friction (plan gate, property access, analysis timeout) is a Log.
 * 5xx and unexpected failures stay Issues.
 */
export const reportFunnelFailure = (
  action: FunnelAction,
  error: unknown,
  scope: ObservabilityScope,
  attributes?: FunnelEventAttributes,
) => {
  if (error instanceof FunnelAnalysisBlockedError) return;

  const status = getHttpStatus(error);

  if (status === 402) {
    logFeatureEvent({
      feature: "funnels",
      event: "plan_limit_reached",
      level: "warn",
      message: "Funnels: plan limit reached",
      scope,
      dedupKey: `plan-limit:${action}:${scope.propertyId ?? "unknown"}`,
      attributes: { funnel_action: action, http_status: 402, ...attributesFor(attributes) },
    });
    return;
  }

  if (isFunnelAccessDenied(error)) {
    logFeatureEvent({
      feature: "funnels",
      event: "access_denied",
      level: "warn",
      message: `Funnels: ${action} access denied`,
      scope,
      dedupKey: `access-denied:${action}:${scope.propertyId ?? "unknown"}`,
      attributes: {
        funnel_action: action,
        http_status: status,
        ...attributesFor(attributes),
      },
    });
    return;
  }

  if (action === "run_analysis" && isFunnelAnalysisTimeout(error)) {
    logFeatureEvent({
      feature: "funnels",
      event: "analysis_timeout",
      level: "warn",
      message: "Funnels: analysis timed out",
      scope,
      dedupKey: `analysis-timeout:${scope.propertyId ?? "unknown"}`,
      attributes: {
        funnel_action: action,
        http_status: status,
        ...attributesFor(attributes),
      },
    });
    return;
  }

  reportFeatureIssue({
    feature: "funnels",
    error,
    action,
    message: `Funnels: ${action} failed`,
    scope,
    context: { ...attributesFor(attributes), http_status: status },
  });
};
